import { useEffect, useMemo, useState } from 'react';

import { CheckIcon, FileIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import useFetchEmailAttachments from 'hooks/useFetchEmailAttachments';
import { useToast } from 'hooks/useToaster';
import { saveLoadDocuments } from 'lib/api/saveLoadDocuments';
import { ThreeGDocumentTypeSelect } from 'pages/QuoteView/LoadBuilding/ThreeGSectionForms/ThreeGDocumentTypeSelect';
import { ThreeGDocumentMessageType } from 'pages/QuoteView/LoadBuilding/ThreeGSectionForms/constants';
import { Email } from 'types/Email';
import { Attachment } from 'types/EmailAttachment';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

type SaveAttachmentsToThreeGProps = {
  loadId: number;
  email: Maybe<Email>;
};

const SUPPORTED_EXTENSION = /\.(pdf|png|jpe?g|tiff?)$/i;

function isSupportedAttachment(attachment: Attachment): boolean {
  const mimeType = (attachment.mimeType || '').toLowerCase();
  const fileName =
    attachment.originalFileName || attachment.transformedFileName || '';
  if (SUPPORTED_EXTENSION.test(fileName)) {
    return true;
  }
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/jpeg') ||
    mimeType.startsWith('image/jpg') ||
    mimeType.startsWith('image/png') ||
    mimeType.startsWith('image/tiff')
  );
}

const SaveAttachmentsToThreeG: React.FC<SaveAttachmentsToThreeGProps> = ({
  loadId,
  email,
}) => {
  const { toast } = useToast();
  const emailExternalId = email?.externalID;
  const { response, isLoading } = useFetchEmailAttachments(emailExternalId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [messageType, setMessageType] = useState<ThreeGDocumentMessageType>(
    ThreeGDocumentMessageType.POD
  );
  const [isSaving, setIsSaving] = useState(false);

  // Reset selection/saved state when the load or email context changes so
  // stale IDs from a previous thread/load cannot enable Save incorrectly.
  useEffect(() => {
    setSelectedIds(new Set());
    setSavedIds(new Set());
    setMessageType(ThreeGDocumentMessageType.POD);
    setIsSaving(false);
  }, [loadId, emailExternalId]);

  const attachments = useMemo(() => {
    const all = response?.attachments ?? [];
    return all.filter(
      (attachment) =>
        !attachment.isInline &&
        !attachment.isInSenderSignature &&
        isSupportedAttachment(attachment)
    );
  }, [response?.attachments]);

  if (!emailExternalId) {
    return null;
  }

  if (isLoading) {
    return (
      <Flex justify='center' gap='xs' className='py-2'>
        <Typography variant='body-sm' className='text-neutral-500'>
          Loading attachments...
        </Typography>
        <ButtonLoader />
      </Flex>
    );
  }

  if (!attachments.length) {
    return (
      <Typography variant='body-xs' className='text-neutral-600 italic py-1'>
        No PDF/image attachments available to save to 3G.
      </Typography>
    );
  }

  const toggleAttachment = (attachmentId: string) => {
    if (savedIds.has(attachmentId)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(attachmentId)) next.delete(attachmentId);
      else next.add(attachmentId);
      return next;
    });
  };

  const handleSave = async () => {
    const toSave = attachments.filter(
      (attachment) =>
        selectedIds.has(attachment.externalId) &&
        !savedIds.has(attachment.externalId)
    );
    if (!toSave.length) {
      toast({
        description: 'Select at least one attachment to save.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveLoadDocuments(loadId, {
        emailExternalId: emailExternalId,
        messageType,
        attachments: toSave.map((attachment) => ({
          externalId: attachment.externalId,
          fileName:
            attachment.originalFileName || attachment.transformedFileName,
          mimeType: attachment.mimeType,
        })),
      });

      if (result.isErr()) {
        toast({
          description: result.error.message,
          variant: 'destructive',
        });
        return;
      }

      const nextSaved = new Set(savedIds);
      const failures: string[] = [];

      toSave.forEach((attachment, index) => {
        const row = result.value.results[index];
        const label =
          attachment.originalFileName ||
          attachment.transformedFileName ||
          attachment.externalId;

        if (!row) {
          failures.push(`${label}: No result returned`);
          return;
        }

        if (row.success) {
          nextSaved.add(row.attachmentExternalId || attachment.externalId);
          return;
        }

        failures.push(`${label}: ${row.error || 'Failed to save attachment'}`);
      });

      setSavedIds(nextSaved);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        nextSaved.forEach((id) => next.delete(id));
        return next;
      });

      if (failures.length) {
        toast({
          description: failures.join(' · '),
          variant: 'destructive',
        });
        return;
      }

      toast({
        description: 'Attachment(s) saved to 3G.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectableCount = attachments.filter(
    (attachment) => !savedIds.has(attachment.externalId)
  ).length;

  return (
    <Flex direction='col' gap='sm' className='py-2 border-t border-neutral-200'>
      <Typography variant='body-sm' className='font-medium text-neutral-800'>
        Save attachments to 3G
      </Typography>
      <ThreeGDocumentTypeSelect
        value={messageType}
        onChange={setMessageType}
        disabled={isSaving}
      />
      <Flex direction='col' gap='xs'>
        {attachments.map((attachment) => {
          const attachmentId = attachment.externalId;
          const isSaved = savedIds.has(attachmentId);
          const isSelected = selectedIds.has(attachmentId);
          const label =
            attachment.originalFileName ||
            attachment.transformedFileName ||
            attachmentId;

          return (
            <Flex
              key={attachmentId}
              align='center'
              gap='sm'
              className={cn(
                'rounded px-2 py-1.5',
                isSaved ? 'bg-success-50' : 'hover:bg-neutral-50'
              )}
            >
              <Checkbox
                checked={isSaved || isSelected}
                disabled={isSaved || isSaving}
                onCheckedChange={() => toggleAttachment(attachmentId)}
                aria-label={label}
              />
              <FileIcon className='h-4 w-4 text-neutral-500 shrink-0' />
              <Typography
                variant='body-xs'
                className='truncate flex-1'
                title={label}
              >
                {label}
              </Typography>
              {isSaved && (
                <CheckIcon className='h-4 w-4 text-success-600 shrink-0' />
              )}
            </Flex>
          );
        })}
      </Flex>
      <Button
        type='button'
        size='sm'
        variant='secondary'
        disabled={isSaving || selectableCount === 0 || selectedIds.size === 0}
        buttonNamePosthog={ButtonNamePosthog.SaveAttachmentsToThreeG}
        onClick={handleSave}
      >
        {isSaving ? 'Saving…' : 'Save to 3G'}
      </Button>
    </Flex>
  );
};

export default SaveAttachmentsToThreeG;
